import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  MessageSquare,
  UserCheck,
  Users,
} from 'lucide-react';

import LoadingContainer from 'src/componentes/loading-container';

const GRID_GAP = 16;
const MIN_CARD_WIDTH = 190;
const FALLBACK_GRID_COLUMNS = `repeat(auto-fit, minmax(${MIN_CARD_WIDTH}px, 1fr))`;

const getDivisors = (total) => {
  const divisors = [];
  for (let i = 1; i <= total; i += 1) {
    if (total % i === 0) divisors.push(i);
  }
  return divisors;
};

// Escolhe o maior divisor da quantidade de cards que cabe na largura
const getEvenColumns = (width, total) => {
  if (!width || total <= 0) return null;

  const fit = Math.max(
    1,
    Math.floor((width + GRID_GAP) / (MIN_CARD_WIDTH + GRID_GAP))
  );
  const candidates = getDivisors(total).filter((divisor) => divisor <= fit);

  return candidates.length ? candidates[candidates.length - 1] : 1;
};

const styles = {
  grid: {
    display: 'grid',
    gap: GRID_GAP,
    marginBottom: 24,
  },
  card: {
    backgroundColor: '#fff',
    border: '1px solid #eef0f3',
    borderRadius: 12,
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
    padding: '18px 20px',
  },
  topRow: {
    alignItems: 'center',
    display: 'flex',
    gap: 12,
  },
  iconBox: {
    alignItems: 'center',
    borderRadius: 8,
    display: 'flex',
    flexShrink: 0,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  value: {
    color: '#101828',
    fontSize: 30,
    fontWeight: 700,
    lineHeight: 1,
  },
  label: {
    color: '#667085',
    fontSize: 14,
    lineHeight: '18px',
    marginTop: 12,
  },
  loadingWrapper: {
    minHeight: 92,
    marginBottom: 24,
  },
};

const toNumber = (value) =>
  Number.isFinite(Number(value)) ? Number(value) : 0;

function StatCard({ icon: Icon, label, value, color, background }) {
  return (
    <div style={styles.card}>
      <div style={styles.topRow}>
        <div style={{ ...styles.iconBox, backgroundColor: background, color }}>
          <Icon size={18} />
        </div>
        <div style={styles.value}>{value}</div>
      </div>
      <div style={styles.label}>{label}</div>
    </div>
  );
}

function EstatisticasCards({ estatisticas, isLoading }) {
  const intl = useIntl();
  const gridRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(0);

  useEffect(() => {
    const node = gridRef.current;

    if (!node || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      setGridWidth(entries[0]?.contentRect?.width || 0);
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, [estatisticas]);

  if (isLoading) {
    return <LoadingContainer isLoading style={styles.loadingWrapper} />;
  }

  if (!estatisticas) return null;

  const convidados = toNumber(estatisticas.participantesConvidados);
  const respostas = toNumber(estatisticas.respostasRecebidas);
  const pendentes = Math.max(convidados - respostas, 0);
  const taxaParticipacao = convidados > 0 ? respostas / convidados : 0;
  const diasRestantes = Math.max(toNumber(estatisticas.diasUteisRestantes), 0);
  const comentarios = toNumber(estatisticas.totalComentarios);

  const cards = [
    {
      key: 'convidados',
      icon: Users,
      color: '#2f80ed',
      background: '#e7f0fd',
      label: intl.formatMessage({ id: 'labelParticipantes' }),
      value: convidados,
    },
    {
      key: 'respostas',
      icon: ClipboardCheck,
      color: '#27ae60',
      background: '#e6f7ee',
      label: intl.formatMessage({ id: 'labelRespostasRecebidas' }),
      value: respostas,
    },
    {
      key: 'pendentes',
      icon: ClipboardList,
      color: '#ef7d2e',
      background: '#fdeee2',
      label: intl.formatMessage({ id: 'labelRespostasPendentes' }),
      value: pendentes,
    },
    {
      key: 'taxa',
      icon: UserCheck,
      color: '#17b8c4',
      background: '#e3f7f9',
      label: intl.formatMessage({ id: 'labelTaxaParticipacao' }),
      value: intl.formatNumber(taxaParticipacao, {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
    },
    {
      key: 'diasRestantes',
      icon: CalendarDays,
      color: '#ef7d2e',
      background: '#fdeee2',
      label: intl.formatMessage({ id: 'labelDiasRestantes' }),
      value: diasRestantes,
    },
    {
      key: 'comentarios',
      icon: MessageSquare,
      color: '#27ae60',
      background: '#e6f7ee',
      label: intl.formatMessage({ id: 'labelComentarios' }),
      value: comentarios,
    },
  ];

  const columns = getEvenColumns(gridWidth, cards.length);
  const gridTemplateColumns = columns
    ? `repeat(${columns}, minmax(0, 1fr))`
    : FALLBACK_GRID_COLUMNS;

  return (
    <div ref={gridRef} style={{ ...styles.grid, gridTemplateColumns }}>
      {cards.map((card) => (
        <StatCard
          key={card.key}
          icon={card.icon}
          label={card.label}
          value={card.value}
          color={card.color}
          background={card.background}
        />
      ))}
    </div>
  );
}

export default EstatisticasCards;
